import dayjs from 'dayjs';
import { ThirdPartyModule } from 'i18next';
import numbro from 'numbro';

import { CalendarStrings, LocalisedStrings, MapLocales, mapLocales, PSEUDO_LOCALE_CODE } from './util/format';
import { pseudoLocale as numbroPsuedoLocale } from './util/numbro';
import { stringify } from './util/stringify';

const modifyDayJSLocale = (
  language: string,
  locale: ILocale['relativeTime'],
  localisedStrings: LocalisedStrings,
  calendar: CalendarStrings
) => {
  dayjs.updateLocale(language, {
    relativeTime: {
      ...locale,
      future: function (number: string) {
        return number === localisedStrings.justNow() ? number : localisedStrings.future(number);
      },
      past: function (number: string) {
        return number === localisedStrings.justNow() ? number : localisedStrings.past(number);
      },
      s: localisedStrings.justNow(),
      ss: localisedStrings.justNow(),
      d: localisedStrings.days(1),
      dd: function (number: number) {
        // There is no relative thing for weeks in DayJS so we make it ourselves
        if (number < 7) {
          return localisedStrings.days(number);
        } else if ((number % 7 === 0 && number / 7 === 1) || Math.floor(number / 7) === 1) {
          return localisedStrings.weeks(1);
        }
        return localisedStrings.weeks(Math.floor(number / 7));
      },
      M: localisedStrings.months(1),
      y: localisedStrings.years(1),
    },
    calendar,
  });
};

export const dateHelperI18next: ThirdPartyModule = {
  type: '3rdParty',

  init(i18n) {
    i18n.on('languageChanged', (lng: keyof MapLocales) => {
      const currentLocaleMap = mapLocales[lng] || mapLocales['en-GB'];
      modifyDayJSLocale(
        currentLocaleMap.dayjsLocale,
        currentLocaleMap.relativeTime,
        currentLocaleMap.localisedStrings,
        currentLocaleMap.calendar
      );
      dayjs.locale(currentLocaleMap.dayjsLocale);
    });
  },
};

export const numbroI18next: ThirdPartyModule = {
  type: '3rdParty',

  init(i18n) {
    i18n.on('languageChanged', (lng) => {
      try {
        numbro.setLanguage(lng === PSEUDO_LOCALE_CODE ? numbroPsuedoLocale.languageTag : lng);
      } catch (error) {
        console.warn(error);
        numbro.setLanguage('en');
      }
    });
  },
};

// Warning: this augments the interface whether or not the stringify patch is used.
declare module 'i18next' {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- extending external lib
  interface i18n {
    stringify: typeof stringify;
  }
}

export const stringifyPatch: ThirdPartyModule = {
  type: '3rdParty',

  init(i18n) {
    Object.defineProperty(i18n, 'stringify' as const, { value: stringify });
  },
};

export const formatWithT: ThirdPartyModule & { initialised: boolean } = {
  type: '3rdParty',
  initialised: false,

  init(i18n) {
    // Don't reinitialise (only something we redo in tests) - only need to do this once
    if (!this.initialised) {
      // This ensures that `this` inside `formatFunction` always has an `t` function available
      Object.defineProperty(i18n.services.interpolator, 't', { value: i18n.t });
      this.initialised = true;
    }
  },
};

export const setDocumentLanguage: ThirdPartyModule = {
  type: '3rdParty',

  init(i18n) {
    i18n.on('languageChanged', (lng) => {
      document.documentElement.setAttribute('lang', lng);
    });
  },
};
